import {
  Background,
  BackgroundVariant,
  Edge,
  MiniMap,
  Node,
  ReactFlow,
  ReactFlowProps,
  SelectionMode,
} from "@xyflow/react";
import { memo, ReactNode } from "react";
import * as styles from "../new-flow-editor.module.css";
import { BadgePositionProvider } from "../utils/badge-position-context";
import { CustomConnectionLine } from "./custom-connection-line";
import { CustomFlowEdge } from "./custom-flow-edge";
import { FlowStepNode, NodeData } from "./flow-step-node";
import { ZoomUpdater } from "./zoom-updater";

const GRID_SIZE = 24;

const nodeTypes = { flowStep: FlowStepNode };
const edgeTypes = { customFlow: CustomFlowEdge };

type FlowCanvasProps = {
  nodes: Node<NodeData>[];
  edges: Edge[];
  mode: "edit" | "readonly";
  children?: ReactNode;
} & Omit<
  ReactFlowProps<Node<NodeData>, Edge>,
  | "nodes"
  | "edges"
  | "nodeTypes"
  | "edgeTypes"
  | "proOptions"
  | "maxZoom"
  | "minZoom"
  | "children"
>;

export const FlowCanvas = memo(function FlowCanvas({
  nodes,
  edges,
  mode,
  children,
  className,
  ...reactFlowProps
}: FlowCanvasProps) {
  const isEdit = mode === "edit";

  return (
    <BadgePositionProvider>
      <ReactFlow
        className={className || styles.flowEditor}
        connectionLineComponent={isEdit ? CustomConnectionLine : undefined}
        connectionRadius={isEdit ? 120 : undefined}
        defaultViewport={isEdit ? { x: 0, y: 0, zoom: 1 } : undefined}
        deleteKeyCode={isEdit ? null : undefined}
        edges={edges}
        edgeTypes={edgeTypes}
        elementsSelectable={isEdit ? true : !!reactFlowProps.onNodeClick}
        maxZoom={2}
        minZoom={0.2}
        nodes={nodes}
        nodesConnectable={isEdit}
        nodesDraggable={isEdit ? reactFlowProps.nodesDraggable : false}
        nodeTypes={nodeTypes}
        panOnScroll={!isEdit}
        proOptions={{ hideAttribution: true }}
        selectionKeyCode={isEdit ? null : undefined}
        selectionMode={isEdit ? SelectionMode.Partial : undefined}
        zoomOnDoubleClick={isEdit ? undefined : false}
        {...reactFlowProps}
      >
        <ZoomUpdater nodes={nodes} />
        {children}
        <Background
          color="var(--redo-component-colors-utility-gray-utility-gray-200)"
          gap={GRID_SIZE}
          size={3}
          variant={BackgroundVariant.Dots}
        />
        <MiniMap
          className={styles.minimap}
          maskColor="rgba(0, 0, 0, 0.1)"
          nodeColor="#f9fafb"
          nodeStrokeColor="#6b7280"
          nodeStrokeWidth={1}
        />
      </ReactFlow>
    </BadgePositionProvider>
  );
});
